import { Amplify } from "aws-amplify";
import { cognitoUserPoolsTokenProvider } from "aws-amplify/auth/cognito";

// US-176 — the PIN fast re-auth flow signs in on its OWN Cognito app client.
//
// Why: user-pool ID tokens carry no `cognito:amr`, so the backend cannot tell
// a PIN session from a full password sign-in by any claim. It CAN by the
// token's `aud` — the app client that issued it — which Cognito enforces: the
// main client only allows password (SRP) sign-in, the re-auth client only the
// PIN + device-signature challenge, and a refresh token only refreshes on the
// client that issued it (livingsyncs-infra cognito.tf `reauth`).
//
// Amplify v6 has ONE global config, so switching flows means reconfiguring it.
// Two things hold the client id and BOTH must move together:
//   - Amplify's resource config — what signIn()/confirmSignIn() send; and
//   - the token provider's own auth config — which names the storage keys
//     (`CognitoIdentityServiceProvider.<clientId>.…`) and the client every
//     refresh goes to. A second Amplify.configure() does NOT update it (nor
//     reset its key-value storage — verified in aws-amplify 6.17's
//     initSingleton), so it is set explicitly here.
//
// The active client is remembered across restarts: a PIN session's tokens are
// stored under the re-auth client's keys, so booting on the main client would
// find no session (and a refresh on the wrong client would fail).
//
// Only the app portal (frontend: web, desktop, iPad shell) calls
// initCognitoClients(). In admin/auditor nothing is registered and
// selectCognitoClient("main") is a no-op.

export type CognitoClientKind = "main" | "reauth";

const ACTIVE_CLIENT_KEY = "ls.auth.cognitoClient";

type CognitoBase = {
  userPoolId: string;
  loginWith: { email: boolean };
  mfa: { status: "on"; totpEnabled: boolean };
};

let registered: {
  base: CognitoBase;
  clientIds: { main: string; reauth?: string };
  active: CognitoClientKind;
} | null = null;

function readPersistedKind(): CognitoClientKind {
  try {
    return window.localStorage.getItem(ACTIVE_CLIENT_KEY) === "reauth"
      ? "reauth"
      : "main";
  } catch {
    return "main";
  }
}

function persistKind(kind: CognitoClientKind): void {
  try {
    window.localStorage.setItem(ACTIVE_CLIENT_KEY, kind);
  } catch {
    // Unavailable storage only costs a restart landing on the main client —
    // the PIN session then reads as signed out, never as a full sign-in.
  }
}

function configureFor(kind: CognitoClientKind): void {
  if (!registered) return;
  const userPoolClientId =
    kind === "reauth" ? registered.clientIds.reauth : registered.clientIds.main;
  if (!userPoolClientId) return;
  const auth = { Cognito: { ...registered.base, userPoolClientId } };
  Amplify.configure({ ...Amplify.getConfig(), Auth: auth });
  cognitoUserPoolsTokenProvider.setAuthConfig(auth);
  registered.active = kind;
}

/**
 * Configure Amplify at app boot on whichever client the last session used.
 * Replaces the app's inline `Amplify.configure({ Auth })`. Call it BEFORE
 * swapping the token provider's key-value storage: the FIRST configure
 * installs Amplify's default storage.
 */
export function initCognitoClients(options: {
  base: CognitoBase;
  mainClientId: string;
  reauthClientId?: string;
}): void {
  const reauth = options.reauthClientId || undefined;
  registered = {
    base: options.base,
    clientIds: { main: options.mainClientId, reauth },
    active: "main",
  };
  const kind = readPersistedKind() === "reauth" && reauth ? "reauth" : "main";
  const auth = {
    Cognito: {
      ...options.base,
      userPoolClientId: kind === "reauth" ? reauth! : options.mainClientId,
    },
  };
  Amplify.configure({ Auth: auth });
  // Only the FIRST configure() hands the token provider its config; set it
  // explicitly so a re-run (dev HMR, tests) can't leave it on a stale client.
  cognitoUserPoolsTokenProvider.setAuthConfig(auth);
  registered.active = kind;
}

/** Whether this build knows a re-auth client to sign PIN sessions in on. */
export function isReauthClientConfigured(): boolean {
  return Boolean(registered?.clientIds.reauth);
}

/**
 * Point Amplify at the client a sign-in is about to use. Call immediately
 * BEFORE `signIn()`: "main" for a password sign-in, "reauth" for a PIN one.
 *
 * Leaving a client drops any tokens still stored under it (local clear only —
 * normally there are none, since sign-out already cleared them), so a stale
 * session on the other client can't linger on a shared device.
 */
export async function selectCognitoClient(
  kind: CognitoClientKind,
): Promise<void> {
  if (!registered) return;
  if (kind === "reauth" && !registered.clientIds.reauth) {
    throw new Error("PIN sign-in isn't set up for this app build.");
  }
  if (registered.active !== kind) {
    await clearLocalSession();
    configureFor(kind);
  }
  persistKind(kind);
}

/**
 * Drop whatever tokens are stored for the ACTIVE client — storage only, no
 * network. Unlike Amplify's signOut(), which calls Cognito's revoke endpoint
 * BEFORE it clears anything and can hang after an iPad lock/wake, this always
 * settles promptly.
 */
export async function clearLocalSession(): Promise<void> {
  try {
    await cognitoUserPoolsTokenProvider.tokenOrchestrator.clearTokens();
  } catch {
    // Nothing stored, or storage unavailable — nothing to leave behind.
  }
}

/** Test-only: forget the registration between tests. */
export function __resetCognitoClientsForTests(): void {
  registered = null;
}
